import { NS_QUESTIONNAIRE } from '@/i18n/namespaces';
import { getPreferredLang } from '@/utils/preferredLang';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetQuestionnaireQuery, usePostAssessmentMutation } from '../apis';
import { APP_ID } from '../constants';
import { Gender, PaymentMode, Relationship } from '../enums';
import { useAppDispatch, useAppSelector } from '../reducers';
import {
  resetState,
  updateFormValues,
  updatePremiums,
  updateUwRemarks,
  updateisDeclined,
} from '../reducers/application';
import { questionnaireSchema } from '../schemas';
import { QuestionnaireFormValues } from '../types/form';
import { useRoute } from './useRoute';
import { pushDataLayer } from '@/utils/googleAnalytics';
import { useGAFactory } from './useGAFactory';

export const useQuestionnaire = () => {
  const { t, i18n } = useTranslation(NS_QUESTIONNAIRE);
  const application = useAppSelector((state) => state.application);
  const dispatch = useAppDispatch();
  const { redirect } = useRoute();
  const [isModalOpen, setModalOpen] = useState(false);
  const GAFactory = useGAFactory();

  useEffect(() => {
    if (!application.formValues?.quotation) {
      redirect('quotation');
    }
  }, [application.formValues?.quotation]);

  const handleModalClose = () => {
    setModalOpen(false);
    redirect('quotation');
    dispatch(resetState());
  };

  const formMethods = useForm<QuestionnaireFormValues>({
    resolver: yupResolver(questionnaireSchema),
    defaultValues: application.formValues.questionnaire,
  });

  const nric = application.formValues.quotation?.life_insured.nric ?? '';
  const name = application.formValues.quotation?.life_insured.name ?? '';
  const relationship = application.formValues.quotation?.life_insured
    .relationship as Relationship;
  const gender = application.formValues.quotation?.life_insured
    .gender as Gender;
  const paymentMode = application.paymentMode;
  const amount =
    paymentMode === PaymentMode.Annually
      ? (application.premiums?.yearlyTotalPremium ?? 0)
      : (application.premiums?.monthlyTotalPremium ?? 0);

  const { data: getQuestionnaireResponse, isLoading: isLoadingQuestionnaire } =
    useGetQuestionnaireQuery({
      appId: APP_ID,
      preferredLang: getPreferredLang(i18n.language),
      refId: application.refId ?? '',
      includedLiamQuestion: false,
    });

  const [postAssessment, { isLoading: isLoadingAssessment }] =
    usePostAssessmentMutation();

  const applicant = useMemo(() => {
    return getQuestionnaireResponse?.data.applicants?.[0];
  }, [getQuestionnaireResponse]);

  const questionDetails = [...(applicant?.questionDetails ?? [])].sort(
    ({ questionNo: a }, { questionNo: b }) => a - b,
  );
  const heightQuestion = questionDetails.find(
    ({ question }) => question === t('height'),
  );
  const weightQuestion = questionDetails.find(
    ({ question }) => question === t('weight'),
  );

  const questionnaires =
    questionDetails
      .filter(({ header }) => header === t('healthHeader'))
      .map(({ questionId, question, header }) => ({
        id: questionId,
        header: header,
        content: question,
      })) ?? t('questionnaires', { returnObjects: true });

  const [notEligible, setNotEligible] = useState(false);

  const onSubmit = async (formValues: QuestionnaireFormValues) => {
    dispatch(
      updateFormValues({
        questionnaire: formValues,
      }),
    );

    try {
      const result = await postAssessment({
        appId: APP_ID,
        refId: application.refId ?? '',
        applicants: [
          {
            id: applicant?.id ?? 1,
            answers: [
              {
                questionId: heightQuestion?.questionId ?? '',
                header: heightQuestion?.header ?? '',
                answer: formValues.height.toString(),
              },
              {
                questionId: weightQuestion?.questionId ?? '',
                header: weightQuestion?.header ?? '',
                answer: formValues.weight.toString(),
              },
              ...formValues.declarations.map(({ id, ...answer }) => ({
                questionId: id,
                ...answer,
              })),
            ],
          },
        ],
      }).unwrap();
      const status = result.data?.applicants?.[0].uwResult === 'Declined';
      const uwRemarks = result.data.applicants?.[0].uwRemarks ?? '';
      pushDataLayer(
        GAFactory('health_submit_health_declaration', {
          planCode: application.planCode,
          paymentModeFormValue: application.paymentMode,
          deductible: application.deductible,
          QuestionnaireValues: formValues,
        }),
      );

      setNotEligible(status);
      dispatch(updateisDeclined(status));
      dispatch(updateUwRemarks(uwRemarks));

      if (!status) {
        dispatch(
          updatePremiums({
            annualTotalPremium: result.data?.annualTotalPremium,
            monthlyTotalPremium: result.data.monthlyTotalPremium,
            yearlyTotalPremium: result.data?.yearlyTotalPremium,
          }),
        );

        redirect('application');
      }
    } catch (error) {
      redirect('error');
    }
  };

  const onBack = () => {
    redirect('plan');
  };

  return {
    nric,
    name,
    relationship,
    gender,
    paymentMode,
    amount,
    questionnaires,
    notEligible,
    formMethods,
    isLoading: isLoadingQuestionnaire || isLoadingAssessment,
    onSubmit,
    onBack,
    handleModalClose,
    isModalOpen,
    setModalOpen,
  };
};
